import { useState, useRef, type KeyboardEvent, useId } from "react";
import { X } from "@repo/ui";
import { cn } from "@repo/utils";
import type { FieldTypeOption } from "./metadata-input";

type Props = {
  label?: string;
  error?: string;
  helperText?: string;
  type: FieldTypeOption;
  initialChoices?: string[];
  onChoicesChange?: (choices: string[]) => void;
  onTryDeleteChoice?: (choice: string) => Promise<boolean>;
  placeholder?: string;
};

const INPUT_TYPE_MAP: Record<FieldTypeOption, React.HTMLInputTypeAttribute> = {
  String: "text",
  Integer: "number",
};

export const ChoicesInput = ({
  label = "Choices",
  type,
  error,
  helperText = "Press Enter to add a choice. Backspace to remove.",
  initialChoices = [],
  onChoicesChange,
  onTryDeleteChoice,
  placeholder = "Add a choice...",
}: Props) => {
  const generatedId = useId();
  const inputId = generatedId;
  const [choices, setChoices] = useState<string[]>(initialChoices);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const localType: React.HTMLInputTypeAttribute = INPUT_TYPE_MAP[type] ?? "text";

  const updateChoices = (newChoices: string[]) => {
    setChoices(newChoices);
    onChoicesChange?.(newChoices);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = inputValue.trim();

      if (trimmedValue && !choices.includes(trimmedValue)) {
        const newChoices = [...choices, trimmedValue];
        updateChoices(newChoices);
        setInputValue("");
      }
    }

    if (e.key === "Backspace" && inputValue === "" && choices.length > 0) {
      e.preventDefault();
      const newChoices = choices.slice(0, -1);
      updateChoices(newChoices);
    }
  };

  const removeChoice = async (indexToRemove: number) => {
    const choiceToRemove = choices[indexToRemove];
    if (!choiceToRemove) return;
    if (onTryDeleteChoice) {
      const canDelete = await onTryDeleteChoice(choiceToRemove);
      if (!canDelete) return;
    }
    const newChoices = choices.filter((_, index) => index !== indexToRemove);
    updateChoices(newChoices);
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div className="w-full">
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={inputId}>
        {label}
      </label>
      <div
        onClick={handleContainerClick}
        className={cn(
          "flex min-h-[59px] cursor-text flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-white p-2",
          {
            "border-red-500 focus-within:border-red-500 focus-within:ring-red-500": !!error,
            "focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500": !error,
          }
        )}
      >
        {choices.map((choice, index) => (
          <div
            key={index}
            className="animate-fadeIn bg-gxe-secondary flex items-center gap-2 rounded-sm px-2.5 py-2 text-sm font-medium text-black"
          >
            <span>{choice}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeChoice(index);
              }}
              className="text-gxe-primary hover:bg-gxe-secondary/90 flex size-4 cursor-pointer items-center justify-center rounded-full bg-white transition-colors focus:outline-none"
              aria-label={`Remove ${choice}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <input
          id={inputId}
          ref={inputRef}
          type={localType}
          value={inputValue}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={choices.length === 0 ? placeholder : ""}
          className="min-w-[120px] flex-grow bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none"
        />
      </div>

      {error && (
        <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-500">
          {error}
        </p>
      )}
      {!error && helperText && (
        <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-gray-500">
          {helperText}
        </p>
      )}
    </div>
  );
};
